import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db, rtdb } from '../firebase/config';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { MessageSquare, Search } from 'lucide-react';
import { getChatUnreadCount, markChatAsRead } from '../firebase/helpers';

const ChatListItem = ({ chat, currentUserId, navigate }) => {
    const otherUserId = chat.participants?.find(p => p !== currentUserId);
    const otherUserProfile = chat.participantProfiles?.[otherUserId] || {};
    const lastMessage = chat.lastMessage || {};
    const unreadCount = getChatUnreadCount(chat, currentUserId);
    const isUnread = unreadCount > 0;
    const [isOnline, setIsOnline] = useState(false);

    useEffect(() => {
        if (!rtdb || !otherUserId) return;
        const statusRef = ref(rtdb, `/status/${otherUserId}`);
        const unsub = onValue(statusRef, (snap) => {
            setIsOnline(snap.val()?.state === 'online');
        });
        return () => unsub();
    }, [otherUserId]);

    return (
        <div
            onClick={() => {
                markChatAsRead(chat.id, currentUserId);
                navigate('chat', { chatId: chat.id });
            }}
            className={`p-3 sm:p-3.5 rounded-2xl border transition-all duration-150 cursor-pointer flex items-center justify-between gap-3 select-none ${

                isUnread
                    ? 'bg-sky-50 dark:bg-sky-950/40 border-sky-400/80 dark:border-sky-500/60 shadow-sm shadow-sky-500/10 ring-1 ring-sky-500/30'
                    : 'bg-white dark:bg-[#111827]/80 border-slate-200/90 dark:border-slate-800/80 hover:border-slate-300 dark:hover:border-slate-700'
            }`}
        >
            <div className="flex items-center gap-3 min-w-0">
                {/* Avatar with Online Pulse Dot */}
                <div className="relative flex-shrink-0">
                    <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-full p-[1.5px] ${isUnread ? 'bg-gradient-to-tr from-sky-400 to-indigo-500 ring-2 ring-sky-400/40' : 'bg-slate-200 dark:bg-slate-700'} shadow-xs`}>
                        <img
                            src={otherUserProfile.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${otherUserId}`}
                            alt=""
                            className="w-full h-full rounded-full object-cover bg-slate-100 dark:bg-slate-800"
                        />
                    </div>
                    {isOnline && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-[#111827] rounded-full shadow-xs"></span>
                    )}
                </div>

                {/* Name & Snippet */}
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                        <h4 className={`font-bold text-xs sm:text-sm truncate ${isUnread ? 'text-sky-600 dark:text-sky-400 font-black' : 'text-slate-900 dark:text-white'}`}>
                            {otherUserProfile.name || 'User'}
                        </h4>
                        <span className="text-[11px] text-slate-400 font-mono truncate">
                            @{otherUserProfile.username || 'user'}
                        </span>
                    </div>
                    <p className={`text-xs truncate mt-0.5 ${isUnread ? 'text-slate-900 dark:text-slate-100 font-bold' : 'text-slate-500 dark:text-slate-400'}`}>
                        {lastMessage.text || (lastMessage.imageUrl ? '📷 Photo' : 'No messages yet')}
                    </p>
                </div>
            </div>

            {/* Right: Unread Counter / Time */}
            <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                {lastMessage.createdAt && (
                    <span className={`text-[10px] font-mono ${isUnread ? 'text-sky-600 dark:text-sky-400 font-bold' : 'text-slate-400'}`}>
                        {lastMessage.createdAt.toDate ? lastMessage.createdAt.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                )}
                {unreadCount > 0 && (
                    <div className="flex items-center gap-1.5">
                        <span className="min-w-[19px] h-[19px] px-1.5 bg-gradient-to-tr from-sky-500 to-indigo-600 text-white font-black text-[10px] rounded-full flex items-center justify-center shadow-sm shadow-sky-500/30">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
};

const ChatsListPage = ({ navigate }) => {
    const { currentUser } = useAuth();
    const [chats, setChats] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!currentUser) return;
        const q = query(
            collection(db, 'chats'),
            where('participants', 'array-contains', currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const chatsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            chatsData.sort((a, b) => (b.lastUpdated?.toMillis() || 0) - (a.lastUpdated?.toMillis() || 0));
            setChats(chatsData);
            setLoading(false);
        }, (err) => {
            console.error("Chats query error:", err);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser]);

    const filteredChats = chats.filter(c => {
        if (!searchTerm.trim()) return true;
        const otherUserId = c.participants.find(p => p !== currentUser.uid);
        const profile = c.participantProfiles?.[otherUserId] || {};
        const term = searchTerm.toLowerCase();
        return (
            (profile.name || '').toLowerCase().includes(term) ||
            (profile.username || '').toLowerCase().includes(term)
        );
    });

    return (
        <div className="space-y-3.5 animate-fade-in pb-16">
            <div className="flex items-center justify-between">
                <h2 className="text-lg sm:text-xl font-black text-slate-900 dark:text-white tracking-tight">
                    Direct Messages
                </h2>
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {chats.length} active
                </span>
            </div>

            {/* Search Option in Message Section */}
            <div className="relative">
                <Search className="absolute left-3.5 top-2.5 sm:top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search messages by name or @username..."
                    className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-2.5 bg-white dark:bg-[#111827]/80 border border-slate-200/90 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/40 transition-all"
                />
            </div>

            {/* Chat List */}
            {loading ? (
                <div className="py-16 text-center space-y-2">
                    <div className="w-7 h-7 mx-auto rounded-full border-2 border-sky-500 border-t-transparent animate-spin" />
                    <p className="text-xs text-slate-400">Loading your conversations...</p>
                </div>
            ) : filteredChats.length > 0 ? (
                <div className="space-y-2">
                    {filteredChats.map(chat => (
                        <ChatListItem
                            key={chat.id}
                            chat={chat}
                            currentUserId={currentUser.uid}
                            navigate={navigate}
                        />
                    ))}
                </div>
            ) : (
                <div className="text-center py-16 bg-white dark:bg-[#111827]/50 border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 space-y-2.5">
                    <div className="w-11 h-11 bg-sky-50 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto text-sky-500 dark:text-sky-400">
                        <MessageSquare className="w-5 h-5" />
                    </div>
                    <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-white">
                        {searchTerm ? 'No conversations matched your search' : 'No messages yet'}
                    </h3>
                    <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                        Start a conversation by tapping the message icon on any post, note, or user profile!
                    </p>
                </div>
            )}
        </div>
    );
};

export default ChatsListPage;
